import { lazy, type ComponentType, type LazyExoticComponent } from "react";
import { logger } from "@/utils/logger";

const RELOAD_TS_KEY = "franklin:chunk-reload-ts";
const RELOAD_COOLDOWN_MS = 10_000;

/**
 * Recognise the errors a browser throws when a dynamically imported chunk
 * cannot be loaded. After a redeploy the hashed filename changes, so a tab on
 * the old build requests a chunk hash that no longer exists. The SPA host
 * serves `index.html` (HTTP 200, `text/html`) in its place, which the browser
 * refuses to evaluate as a module. The wording differs per engine.
 */
export function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return (
    // Chromium
    /Failed to fetch dynamically imported module/i.test(message) ||
    // Firefox
    /error loading dynamically imported module/i.test(message) ||
    // Safari
    /Importing a module script failed/i.test(message) ||
    // MIME mismatch when the SPA fallback returns index.html for a missing .js
    /Expected a JavaScript(?:-or-Wasm)? module script|MIME type of "text\/html"/i.test(
      message
    )
  );
}

/**
 * Wrap a dynamic-import factory so a stale-chunk failure reloads the page once.
 *
 * On a chunk-load failure we reload to pick up the fresh `index.html` and its
 * current chunk hashes (`index.html` is served `must-revalidate`, so the reload
 * fetches the new build). A timestamp in `sessionStorage` caps reloads to one
 * per cooldown window, so a genuinely-missing chunk can't trigger a reload loop
 * — after the second failure within the window the error is re-thrown so the
 * nearest error boundary can show a real message. Non-chunk errors are always
 * re-thrown untouched.
 *
 * Exported separately from {@link lazyWithReload} so the recovery behaviour can
 * be unit-tested without rendering through `React.lazy`/`Suspense`.
 */
export function withChunkReload<T>(
  factory: () => Promise<T>
): () => Promise<T> {
  return () =>
    factory().catch((error: unknown) => {
      if (!isChunkLoadError(error)) throw error;

      let lastReload = 0;
      try {
        lastReload = Number(window.sessionStorage.getItem(RELOAD_TS_KEY)) || 0;
      } catch {
        // sessionStorage can throw in private/locked-down contexts — treat as
        // "never reloaded" and fall through to a reload attempt.
      }

      const now = Date.now();
      if (now - lastReload <= RELOAD_COOLDOWN_MS) {
        // Already reloaded once within the window and the chunk still failed —
        // surface the error rather than looping.
        throw error;
      }

      try {
        window.sessionStorage.setItem(RELOAD_TS_KEY, String(now));
      } catch {
        // Ignore — a failed write just means the cooldown guard is best-effort.
      }

      void logger.warn(
        "ui",
        "chunk-reload",
        "Stale dynamic import detected — reloading to fetch the current build",
        { error: error instanceof Error ? error.message : String(error) }
      );
      window.location.reload();

      // Hang so React keeps showing the Suspense fallback until the reload
      // takes over, instead of flashing the lazy error boundary first.
      return new Promise<T>(() => {
        /* never resolves — the page reload above replaces this document */
      });
    });
}

/**
 * `React.lazy` wrapper that recovers from stale-chunk failures after a deploy.
 *
 * We catch the `import()` rejection directly rather than relying on Vite's
 * `vite:preloadError` event, because the app builds with `modulePreload: false`
 * (see vite.config.ts) and so does not emit that event.
 */
export function lazyWithReload<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>
): LazyExoticComponent<T> {
  return lazy(withChunkReload(factory));
}
